import { z } from 'zod';

export const recordingTrimSchema = z.object({
  enabled: z.boolean(),
  thresholdSeconds: z.number().min(1).max(10),
  keepSeconds: z.number().min(0.5).max(3),
  trimEdges: z.boolean(),
}).refine(value => value.keepSeconds < value.thresholdSeconds, {
  message: 'The retained pause must be shorter than the silence threshold.',
  path: ['keepSeconds'],
});

export type RecordingTrimSettings = z.infer<typeof recordingTrimSchema>;
export const defaultRecordingTrim: RecordingTrimSettings = {
  enabled: false, thresholdSeconds: 1.8, keepSeconds: 0.9, trimEdges: true,
};
export type RecordingInterval = [start: number, end: number];

/** Edit only quiet intervals. Retain breathing room on both sides of each cut. */
export function planRecordingTrim(
  silence: RecordingInterval[], duration: number, settings: RecordingTrimSettings,
): RecordingInterval[] {
  const full: RecordingInterval[] = [[0, duration]];
  if (!settings.enabled || !silence.length || duration <= 0) return full;
  // An all-silent recording has no safe speech boundaries to trim around.
  if (silence.some(([start, end]) => start <= 0.02 && end >= duration - 0.02)) return full;
  const cuts: RecordingInterval[] = [];
  for (const [rawStart, rawEnd] of silence) {
    const start = Math.max(0, rawStart), end = Math.min(duration, rawEnd);
    if (end - start <= settings.thresholdSeconds) continue;
    if (start <= 0.02) {
      if (settings.trimEdges) cuts.push([0, Math.max(0, end - 0.35)]);
    } else if (end >= duration - 0.02) {
      if (settings.trimEdges) cuts.push([Math.min(duration, start + 0.55), duration]);
    } else {
      cuts.push([start + settings.keepSeconds * 4 / 9, end - settings.keepSeconds * 5 / 9]);
    }
  }
  const keep: RecordingInterval[] = [];
  let cursor = 0;
  for (const [start, end] of cuts.sort((a, b) => a[0] - b[0])) {
    if (start > cursor) keep.push([cursor, start]);
    cursor = Math.max(cursor, end);
  }
  if (cursor < duration) keep.push([cursor, duration]);
  return keep.length ? keep : full;
}
